import axios from "axios";
import { firebaseAuth } from "./firebase";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ASSET_BASE = API;

const api = axios.create({ baseURL: API });

// Attach a FRESH Firebase ID token to every request. Firebase ID tokens expire
// after 1 hour; `currentUser.getIdToken()` returns the cached token if still
// valid and transparently refreshes it when near/after expiry — so admin
// sessions never 401 as long as the user stays signed in (no 1-hour logout).
// Falls back to the last window-cached token if the SDK isn't ready yet.
api.interceptors.request.use(async (config) => {
  config.headers = config.headers || {};
  if (!config.headers.Authorization) {
    try {
      const u = firebaseAuth().currentUser;
      if (u) {
        const t = await u.getIdToken();
        if (typeof window !== "undefined") window.__firebase_id_token = t;
        config.headers.Authorization = `Bearer ${t}`;
      } else if (typeof window !== "undefined" && window.__firebase_id_token) {
        config.headers.Authorization = `Bearer ${window.__firebase_id_token}`;
      }
    } catch {
      if (typeof window !== "undefined" && window.__firebase_id_token) {
        config.headers.Authorization = `Bearer ${window.__firebase_id_token}`;
      }
    }
  }
  return config;
});

export const getStats = () => api.get("/stats");
export const getGoogleReviews = () => api.get("/reviews");
export const getPaymentConfig = () => api.get("/payments/config");
export const createPaymentOrder = (payload) => api.post("/payments/create-order", payload);
export const verifyPayment = (payload) => api.post("/payments/verify", payload);
export const getArchitecture = () => api.get("/admin/architecture");
export const refreshArchitecture = () => api.post("/admin/architecture/refresh");
export const getMasterOverview = (state) => api.get("/admin/master", { params: { state } });
export const listMigrations = () => api.get("/admin/migrations");
export const exportFeesCsv = (params) => api.get("/admin/export-fees-csv", { params, responseType: "blob" });
export const feesCsvPreview = (formData) => api.post("/admin/fees-csv/preview", formData);
export const feesCsvPreviewStatus = (jobId) => api.get(`/admin/fees-csv/preview/status/${jobId}`);
export const feesCsvApply = (formData) => api.post("/admin/fees-csv/apply", formData);
export const feesCsvHeaders = (formData) => api.post("/admin/fees-csv/headers", formData);
export const ingestReconSummary = (id, cols) => api.get(`/admin/ingest-recon/${id}/summary`, { params: cols ? { cols } : {} });
export const ingestReconApply = (id, formData) => api.post(`/admin/ingest-recon/${id}/apply`, formData);
export const ingestReconRows = (id, params) => api.get(`/admin/ingest-recon/${id}/rows`, { params });
export const ingestReconExport = (id, params = {}) => api.get(`/admin/ingest-recon/${id}/export.csv`, { params, responseType: "blob" });
export const getIngestJob = (jobId) => api.get(`/ingest/job/${jobId}`);
export const cancelDocIngest = (url) => api.post("/admin/counselling-docs/cancel-ingest", { url });
export const listAllotmentUnmatched = (params) => api.get("/admin/allotment/unmatched", { params });
export const assignAllotmentUnmatched = (payload) => api.post("/admin/allotment/assign-unmatched", payload);
export const llmMatchAllotmentUnmatched = (payload) => api.post("/admin/allotment/llm-match-unmatched", payload);
export const allotmentReconOpen = (payload) => api.post("/admin/allotment-recon/open", payload);
export const orphanReconOpen = () => api.post("/admin/orphan-recon/open");
export const smReviewOpen = (payload) => api.post("/admin/sm-review/open", payload || {});
export const stateDiagnoseStates = () => api.get("/admin/state-diagnose/states");
export const stateDiagnose = (payload) => api.post("/admin/state-diagnose", payload || {});
export const getIngestionConfig = () => api.get("/admin/config/ingestion");
export const updateIngestionConfig = (payload) => api.put("/admin/config/ingestion", payload);
export const getLogTail = (params) => api.get("/admin/logs/tail", { params });
export const ingestReconPatchRow = (id, rowNo, patch) => api.patch(`/admin/ingest-recon/${id}/rows/${rowNo}`, patch);
export const ingestReconRediff = (id) => api.post(`/admin/ingest-recon/${id}/rediff`);
export const ingestReconStates = () => api.get(`/admin/ingest-recon-states`);
export const ingestReconClassifyStatesLlm = (id) => api.post(`/admin/ingest-recon/${id}/classify-states-llm`);
export const ingestReconClassifyStateLlmRow = (id, rowNo) => api.post(`/admin/ingest-recon/${id}/rows/${rowNo}/classify-state-llm`);
export const ingestReconLlmMatch = (id, first_state, state) => api.post(`/admin/ingest-recon/${id}/llm-match`, { first_state, state });
export const ingestReconAcceptSuggestions = (id, state) => api.post(`/admin/ingest-recon/${id}/accept-suggestions`, { state });
export const ingestReconCreateNew = (id, state) => api.post(`/admin/ingest-recon/${id}/create-new`, {}, { params: state ? { state } : {}, timeout: 300000 });
export const ingestReconCreateNewPreview = (id, state) => api.get(`/admin/ingest-recon/${id}/create-new-preview`, { params: state ? { state } : {} });
export const runMigration = (id, apply) => api.post(`/admin/migrations/${id}/run`, { apply }, { timeout: 300000 });
export const runMigrationAsync = (id, apply) => api.post(`/admin/migrations/${id}/run-async`, { apply });
export const getMigrationJob = (jobId) => api.get(`/admin/migrations/job/${jobId}`);
export const markMigrationApplied = (id) => api.post(`/admin/migrations/${id}/mark-applied`);
export const listMigrationBackups = () => api.get("/admin/migrations/backups");
export const deleteMigrationBackup = (name) => api.delete(`/admin/migrations/backups/${encodeURIComponent(name)}`);
// Per-college Seat-Matrix inspector/cleanup (admin): raw rows + provenance, and targeted delete.
export const getCollegeSeatMatrixRows = (code, year) =>
  api.get(`/admin/college/${encodeURIComponent(code)}/seat-matrix/rows`, { params: year != null ? { year } : {} });
export const deleteCollegeSeatMatrixRows = (code, ids) =>
  api.post(`/admin/college/${encodeURIComponent(code)}/seat-matrix/delete`, { ids });
export const deleteCollegeSeatMatrixCategories = (code, rowId, categories) =>
  api.post(`/admin/college/${encodeURIComponent(code)}/seat-matrix/row/${encodeURIComponent(rowId)}/delete-categories`, { categories });
export const startDiaryReingest = (body, year, scope) => api.post(`/admin/diary/reingest`, { body, year, scope });
export const startDiaryReingestFile = (ingestId) => api.post(`/admin/diary/reingest-file`, { ingest_id: ingestId });
export const listDiaryReingest = () => api.get(`/admin/diary/reingest`);
export const getDiaryReingestStatus = (jobId) => api.get(`/admin/diary/reingest/${jobId}`);
export const inspectDiarySeatMatrix = (payload) => api.post(`/admin/diary/seat-matrix/inspect`, payload);
export const deleteDiarySeatMatrix = (payload) => api.post(`/admin/diary/seat-matrix/delete`, payload);
export const cancelDiaryReingest = (jobId) => api.post(`/admin/diary/reingest/${jobId}/cancel`);
export const planDiaryReingest = (body, year, scope) => api.get(`/admin/diary/reingest/plan`, { params: { body, year, scope } });
export const matchReviewGenerate = (payload) => api.post("/admin/match-review/generate", payload);
export const matchReviewJob = (jobId) => api.get(`/admin/match-review/job/${jobId}`);
export const matchReviewList = (source = "mcc_sm") => api.get("/admin/match-review", { params: { source } });
export const matchReviewUpdate = (id, payload) => api.patch(`/admin/match-review/${id}`, payload);
export const matchReviewApply = (payload) => api.post("/admin/match-review/apply", payload, { timeout: 120000 });
export const matchReviewResolveConflict = (id, payload) => api.post(`/admin/match-review/${id}/resolve-conflict`, payload, { timeout: 180000 });
export const masterRawNameClusters = (code, body = "MCC") => api.get(`/admin/master/${encodeURIComponent(code)}/raw-name-clusters`, { params: { body } });
export const masterEnrichDetails = (code, payload = {}) => api.post(`/admin/master/${encodeURIComponent(code)}/enrich-details`, payload);
export const masterCrossWired = () => api.get(`/admin/master/cross-wired`);
export const masterDewire = (code, payload) => api.post(`/admin/master/cross-wired/${encodeURIComponent(code)}/dewire`, payload, { timeout: 180000 });
export const getMasterOrphans = () => api.get("/admin/master/orphans");
export const masterMergePreview = (payload) => api.post("/admin/master/merge-preview", payload);
export const masterMergeApply = (payload) => api.post("/admin/master/apply", payload, { timeout: 120000 });
export const masterMergeRevert = (op_id) => api.post("/admin/master/revert", { op_id }, { timeout: 120000 });
export const getMasterLog = (code) => api.get("/admin/master/log", { params: code ? { code } : {} });
export const getMasterDiff = (op_id) => api.get(`/admin/master/diff/${op_id}`);
export const rebuildCutoffsPreview = (target, course) => api.post("/admin/master/rebuild-cutoffs/preview", { target, course });
export const rebuildCutoffs = (target, course) => api.post("/admin/master/rebuild-cutoffs", { target, course }, { timeout: 300000 });
export const backfillCollegeCodePreview = (body) => api.post("/admin/master/backfill-college-code/preview", { body });
export const backfillCollegeCode = (body) => api.post("/admin/master/backfill-college-code", { body }, { timeout: 300000 });
export const backfillCatNormPreview = (body) => api.post("/admin/master/backfill-cat-norm/preview", { body });
export const backfillCatNorm = (body) => api.post("/admin/master/backfill-cat-norm", { body }, { timeout: 300000 });
export const rebuildFilterCache = (body) => api.post("/admin/rebuild-filter-cache", body ? { body } : {}, { timeout: 300000 });
export const assistantChat = (session_id, message) => api.post("/assistant/chat", { session_id, message }, { timeout: 60000 });
export const assistantTranscribe = (formData) => api.post("/assistant/transcribe", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
export const getSiteConfig = () => api.get("/site-config");
export const updateSiteConfig = (data) => api.put("/admin/site-config", data);
export const updateUsdRate = () => api.post("/admin/update-usd-rate", null, { timeout: 60000 });
export const getDataHealth = () => api.get("/admin/data-health");
export const verifyCollisions = () => api.post("/admin/verify-collisions", null, { timeout: 120000 });
export const verifyMerged = () => api.post("/admin/verify-merged", null, { timeout: 120000 });
export const matchCollisionMasters = () => api.post("/admin/match-collision-masters", null, { timeout: 120000 });
export const feesLlmCheck = (codes) => api.post("/admin/fees-llm-check", { codes }, { timeout: 120000 });
export const feesLlmApply = (proposals) => api.post("/admin/fees-llm-apply", { proposals }, { timeout: 60000 });
export const fetchFeesOfficial = (payload) => api.post("/admin/master/fetch-fees-official", payload, { timeout: 60000 });
export const fetchFeesOfficialJob = (jobId) => api.get(`/admin/master/fetch-fees-official/job/${jobId}`);
export const addFeeSourceUrl = (college_code, url) => api.post("/admin/master/add-fee-source-url", { college_code, url }, { timeout: 70000 });
export const enrichCollegePreview = (college_code, opts = {}) =>
  api.post("/admin/enrich-college-preview",
           { college_code, force_scrape: !!opts.forceScrape },
           { timeout: 180000 });
export const enrichCollegeApply = (college_code, fields) => api.post("/admin/enrich-college-apply", { college_code, fields }, { timeout: 60000 });
export const forceScrapeCollege = (college_code) => api.post("/admin/force-scrape-college", { college_code }, { timeout: 60000 });
export const editCollegeFields = (college_code, fields) => api.post("/admin/edit-college-fields", { college_code, fields }, { timeout: 30000 });
export const syncCollegeLocation = (college_code) => api.post("/admin/college/sync-location", { college_code }, { timeout: 45000 });
export const removeCollegeAlias = (college_code, name, source) => api.post("/admin/remove-college-alias", { college_code, name, source }, { timeout: 15000 });
export const clearNmcName = (college_code) => api.post("/admin/clear-nmc-name", { college_code }, { timeout: 15000 });
export const addMasterAlias = (college_code, name) => api.post("/admin/master/add-alias", { college_code, name }, { timeout: 15000 });
export const setMasterDisplayName = (college_code, name) => api.post("/admin/master/set-display-name", { college_code, name }, { timeout: 15000 });
export const addMasterFees = (payload) => api.post("/admin/master/add-fees", payload, { timeout: 15000 });
export const addMasterCode = (college_code, body, code) => api.post("/admin/master/add-code", { college_code, body, code }, { timeout: 15000 });
export const getInternalCodes = (college_code) => api.get(`/admin/college/${encodeURIComponent(college_code)}/internal-codes`);
export const setCanonicalCode = (college_code, new_code) => api.post(`/admin/college/${encodeURIComponent(college_code)}/set-canonical`, { new_code }, { timeout: 60000 });
export const getCourseCatalog = () => api.get("/admin/courses/catalog", { timeout: 15000 });
export const mergeMasterPreview = (destination_code, source_code) => api.post("/admin/master/merge/preview", { destination_code, source_code }, { timeout: 20000 });
export const mergeMasterApply = (destination_code, source_code) => api.post("/admin/master/merge", { destination_code, source_code }, { timeout: 120000 });
export const mergeMasterUndo = (merge_id) => api.post("/admin/master/merge/undo", { merge_id }, { timeout: 120000 });
export const splitMasterBreakdown = (college_code) => api.get("/admin/master/split/breakdown", { params: { college_code }, timeout: 20000 });
// apply returns as soon as the core split is committed; the heavy derived rebuild
// runs as a background job (polled via jobsStatus), so a short timeout is fine.
export const splitMaster = (payload) => api.post("/admin/master/split", payload, { timeout: payload && payload.dry_run ? 20000 : 60000 });
export const applyNmcCollegeType = (codes) => api.post("/admin/apply-nmc-college-type", { codes }, { timeout: 30000 });
export const verifyDeemedPreview = (college_code) => api.post("/admin/verify-deemed-preview", { college_code }, { timeout: 120000 });
export const verifyDeemedApply = (college_code, new_status, affiliate_university) => api.post("/admin/verify-deemed-apply", { college_code, new_status, affiliate_university }, { timeout: 60000 });
export const getMccQuotaOptions = () => api.get("/mcc-quota-options");
export const getScrapeCategories = () => api.get("/admin/config/scrape-categories");
export const putScrapeCategories = (categories, ttl) => api.put("/admin/config/scrape-categories", { categories, ttl }, { timeout: 15000 });
export const listScrapedPages = (q = "", skip = 0, limit = 5) => api.get("/admin/scraped-pages", { params: { q, skip, limit } });
export const getFilterOptions = (params) => api.get("/filter-options", { params });
export const getColleges = (params) => api.get("/colleges", { params });
export const getCollegesGeo = () => api.get("/colleges/geo");
export const getFeaturedColleges = () => api.get("/colleges/featured");
export const setCollegeBanner = (code, idx) => api.post(`/colleges/${code}/set-banner`, null, { params: { idx } });
export const deleteCollegeImage = (code, idx) => api.delete(`/admin/colleges/${code}/images/${idx}`);
export const getCollege = (id) => api.get(`/colleges/${id}`);
export const createCollege = (data) => api.post("/colleges", data);
export const updateCollege = (id, data) => api.put(`/colleges/${id}`, data);
export const deleteCollege = (id) => api.delete(`/colleges/${id}`);
export const getSeatMatrix = (params) => api.get("/seat-matrix", { params });
export const createSeatMatrix = (data) => api.post("/seat-matrix", data);
export const getCounsellingResults = (params) => api.get("/counselling-results", { params });
export const createCounsellingResult = (data) => api.post("/counselling-results", data);
export const bulkCreateCounsellingResults = (data) => api.post("/counselling-results/bulk", data);
export const getCounsellingRounds = (params) => api.get("/counselling-rounds", { params });
export const createOrUpdateRound = (data) => api.post("/counselling-rounds", data);
export const predictColleges = (data) => api.post("/predict", data);
export const getAIRecommendation = (data) => api.post("/ai-recommend", data);
export const getUnfilledSeats = (params) => api.get("/unfilled-seats", { params });
export const seedData = () => api.post("/seed");
export const forceSeedData = () => api.post("/seed/force");

// PDF Ingestion
export const detectPdfMetadata = (formData) => api.post("/ingest/detect", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 30000,
});
export const ingestPdfUpload = (formData) => api.post("/ingest/upload", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 30000,
});
export const ingestPdfUrl = (data) => api.post("/ingest/url", data, { timeout: 30000 });
export const getIngestionJobStatus = (jobId) => api.get(`/ingest/job/${jobId}`);
export const getIngestionHistory = () => api.get("/ingest/history");
export const getIngestionColleges = (params) => api.get("/ingest/colleges", { params });
export const getRawAllotments = (params) => api.get("/raw-allotments", { params });
export const clearRawAllotments = (params) => api.delete("/admin/raw-allotments", { params });

// MCC-specific ingestion
export const ingestMccSeatMatrix = (formData) => api.post("/ingest/mcc/seat-matrix", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 120000,
});
export const ingestMccAllotment = (formData) => api.post("/ingest/mcc/allotment", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 30000,
});

// Maharashtra-specific ingestion
export const ingestMhSeatMatrix = (formData) => api.post("/ingest/mh/seat-matrix", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 120000,
});
export const ingestMhAllotment = (formData) => api.post("/ingest/mh/allotment", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 30000,
});

// Kerala-specific ingestion
export const ingestKeralaAllotment = (formData) => api.post("/ingest/kerala/allotment", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 30000,
});

// Kerala merit (rank) list → merit_list (true NEET AIR per application no)
export const ingestKeralaMerit = (formData) => api.post("/ingest/kerala/merit", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 30000,
});

// Telangana-specific ingestion
export const ingestTelanganaGovtAllotment = (formData) => api.post("/ingest/telangana/govt/allotment", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 30000,
});
export const ingestTelanganaMgmtAllotment = (formData) => api.post("/ingest/telangana/mgmt/allotment", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 30000,
});
export const ingestTelanganaGovtSeatMatrix = (formData) => api.post("/ingest/telangana/govt/seat-matrix", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 30000,
});
export const ingestTelanganaMgmtSeatMatrix = (formData) => api.post("/ingest/telangana/mgmt/seat-matrix", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 30000,
});

// AP-specific ingestion
export const ingestAPGovtAllotment = (formData) => api.post("/ingest/ap/govt/allotment", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 30000,
});
export const ingestAPMgmtAllotment = (formData) => api.post("/ingest/ap/mgmt/allotment", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 30000,
});
export const ingestAPGovtSeatMatrix = (formData) => api.post("/ingest/ap/govt/seat-matrix", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 30000,
});
export const ingestAPMgmtSeatMatrix = (formData) => api.post("/ingest/ap/mgmt/seat-matrix", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 30000,
});

// West Bengal
export const ingestWBAllotment = (formData) => api.post("/ingest/wb/allotment", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 30000,
});
export const ingestWBSeatMatrix = (formData) => api.post("/ingest/wb/seat-matrix", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 60000,
});

// Bihar
export const ingestBiharAllotment = (formData) => api.post("/ingest/bihar/allotment", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 30000,
});
export const ingestBiharSeatMatrix = (formData) => api.post("/ingest/bihar/seat-matrix", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 60000,
});

// Haryana
export const ingestHaryanaAllotment = (formData) => api.post("/ingest/haryana/allotment", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 30000,
});
export const ingestHaryanaSeatMatrix = (formData) => api.post("/ingest/haryana/seat-matrix", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 60000,
});

export const ingestUPAllotment = (formData) => api.post("/ingest/up/allotment", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 60000,
});
export const ingestUPSeatMatrix = (formData) => api.post("/ingest/up/seat-matrix", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 60000,
});

export const ingestRajasthanAllotment = (formData) => api.post("/ingest/rajasthan/allotment", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 120000,
});
export const ingestRajasthanSeatMatrix = (formData) => api.post("/ingest/rajasthan/seat-matrix", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 60000,
});

// Jharkhand
export const ingestJharkhandAllotment = (formData) => api.post("/ingest/jharkhand/allotment", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 120000,
});
export const ingestJharkhandSeatMatrix = (formData) => api.post("/ingest/jharkhand/seat-matrix", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 60000,
});

// Bulk State Ingestion
export const bulkUploadZip = (formData) => api.post("/ingest/bulk/upload-zip", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 120000,
});
export const bulkUploadMultiZip = (formData) => api.post("/ingest/bulk/upload-multi-zip", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 300000,
});
export const bulkStartIngestion = (data) => api.post("/ingest/bulk/start", data, { timeout: 30000 });
export const bulkGetStatus = (batchId) => api.get(`/ingest/bulk/status/${batchId}`);
export const classifySinglePdf = (formData) => api.post("/ingest/single/classify", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 120000,
});
// One-call admin single-PDF ingest (classify + dispatch via the SAME engine as bulk,
// in one request/worker → routes every body + doc-type incl merit, returns job_id).
export const ingestSinglePdf = (formData) => api.post("/ingest/single/ingest", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 120000,
});

// NMC Seat Matrix
export const nmcUploadSeatMatrix = (formData) => api.post("/ingest/nmc/seat-matrix", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 120000,
});
export const nmcApplyMatches = (data) => api.post("/ingest/nmc/apply", data, { timeout: 60000 });
export const nmcListBatches = () => api.get("/nmc/review-batches");
export const nmcGetBatch = (batchId) => api.get(`/nmc/review/${batchId}`);
export const nmcUpdateAction = (batchId, data) => api.patch(`/nmc/review/${batchId}/action`, data);
export const nmcRematchBatch = (batchId) => api.post(`/nmc/review/${batchId}/rematch`, {}, { timeout: 120000 });
export const nmcLlmVerify = (batchId) => api.post(`/nmc/review/${batchId}/llm-verify`, {}, { timeout: 300000 });
export const nmcRescanNew = (batchId) => api.post(`/nmc/review/${batchId}/rescan-new`, {}, { timeout: 120000 });
export const nmcLlmMatchNew = (batchId) => api.post(`/nmc/review/${batchId}/llm-match-new`, {}, { timeout: 300000 });

// Fees CSV
export const feesUploadCsv = (formData) => api.post("/ingest/fees/csv", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 300000,
});
export const feesApplyMatches = (data) => api.post("/ingest/fees/apply", data, { timeout: 60000 });



// Seat Matrix
export const uploadSeatMatrix = (formData) => api.post("/seat-matrix/upload", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 30000,
});
export const uploadProvisionalSeatMatrix = (formData) => api.post("/seat-matrix/upload-provisional", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 30000,
});
export const getSeatMatrixDetail = (params) => api.get("/seat-matrix/detail", { params });
export const getUnfilledSeatsDetail = (params) => api.get("/unfilled-seats/detail", { params });

export const formatFees = (fees) => {
  if (!fees && fees !== 0) return "N/A";
  if (fees >= 100000) return `${(fees / 100000).toFixed(1)}L`;
  if (fees >= 1000) return `${(fees / 1000).toFixed(0)}K`;
  return `${fees}`;
};

export const formatRound = (rnd) => {
  if (!rnd && rnd !== 0) return "—";
  if (rnd === 0)                    return "Seat Matrix";
  if (rnd === 50)                   return "Full Seat Matrix";
  if (rnd > 50 && rnd <= 57)        return `Provisional Round ${rnd - 50}`;
  if (rnd === 100)                  return "Stray Round";
  if (rnd === 101)                  return "Stray Round 1";
  if (rnd > 101 && rnd < 200)       return `Stray Round ${rnd - 100}`;
  if (rnd === 200)                  return "Special Stray Round";
  if (rnd === 201)                  return "Special Stray Round 1";
  if (rnd > 201)                    return `Special Stray Round ${rnd - 200}`;
  return `Round ${rnd}`;
};

export const CATEGORY_COLORS = {
  GM: "bg-[#09090B] text-white",
  OBC: "bg-[#6043EF] text-white",
  SC: "bg-[#00C04B] text-white",
  ST: "bg-[#FFC107] text-[#09090B]",
  EWS: "bg-[#52525B] text-white",
  PWD: "bg-[#FF2A00] text-white",
  NRI: "bg-[#7C3AED] text-white",
};

export const CHANCE_STYLES = {
  high: "border-[#00C04B] bg-[#00C04B]/10 text-[#00C04B]",
  medium: "border-[#FFC107] bg-[#FFC107]/10 text-[#92700C]",
  low: "border-[#FF2A00] bg-[#FF2A00]/10 text-[#FF2A00]",
};

// ─── Dedup / Merge ──────────────────────────────────────────────────────
export const findDuplicates = (state) => api.post("/admin/find-duplicates", { state });
export const findDuplicatesStatus = (jobId) => api.get(`/admin/find-duplicates/${jobId}`);
export const mergeDuplicates = (master_code, duplicate_codes) => api.post("/admin/merge-duplicates", { master_code, duplicate_codes });
export const fillNewCollegesPreview = (state) => api.post("/admin/fill-new-colleges/preview", { state });
export const runFillNewColleges = (state, codes) => api.post("/admin/fill-new-colleges/run", { state, codes });
export const jobsStatus = (job_ids) => api.post("/admin/jobs-status", { job_ids });

// ─── Agent Chat ─────────────────────────────────────────────────────────
export const agentCreateSession = () => api.post("/agent/sessions");
export const agentGetSessions = () => api.get("/agent/sessions");
export const agentGetSession = (id) => api.get(`/agent/sessions/${id}`);
export const agentActiveJobs = () => api.get(`/agent/jobs/active`);
export const agentStopSession = (id) => api.post(`/agent/sessions/${id}/stop`);
export const agentSendMessage = (id, text, autoMode) => api.post(`/agent/sessions/${id}/message`, { text, auto_mode: !!autoMode }, { timeout: 120000 });
export const agentTier1cKeyword = (id, msgId, keyword) => api.post(`/agent/sessions/${id}/tier1c-keyword`, { msg_id: msgId, keyword });
export const agentUploadFile = (id, file, text, autoMode) => {
  const formData = new FormData();
  formData.append("file", file);
  if (text) formData.append("text", text);
  if (autoMode) formData.append("auto_mode", "true");
  return api.post(`/agent/sessions/${id}/upload`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  });
};

export default api;

// ─── Crawlers ───────────────────────────────────────────────────────────
export const getCrawlerLogs = (type, since) => api.get(`/admin/crawler-logs`, { params: { type, since } });
export const getCrawlerSources = (type) => api.get(`/admin/crawler-sources`, { params: { type } });
export const getMissingCrawlerBodies = () => api.get(`/admin/crawler-sources/missing-bodies`);
export const addCrawlerSource = (payload) => api.post(`/admin/crawler-sources/add`, payload, { timeout: 20000 });
export const updateCrawlerSource = (payload) => api.post(`/admin/crawler-sources/update`, payload, { timeout: 20000 });
export const runCrawler = (payload) => api.post(`/admin/crawlers/run`, payload);
export const enqueueEnrichmentRun = (college_codes, reason = "data_health") => api.post(`/admin/enrichment/enqueue-run`, { college_codes, reason });
export const getCounsellingBoard = (year) => api.get(`/counselling/board`, { params: { year } });
export const getCounsellingLiveBoards = () => api.get(`/counselling/live-boards`);
export const getCounsellingArchivedBoards = () => api.get(`/counselling/archived-boards`);

// Ingestion Diary
export const getIngestions = (params = {}) => api.get(`/admin/ingestions`, { params });
export const getIngestionDetail = (ingestId) => api.get(`/admin/ingestions/${ingestId}`);
export const detectBackfillIngestions = (files, apply = false) => {
  const fd = new FormData();
  files.forEach((f) => fd.append("files", f));
  fd.append("apply", apply ? "true" : "false");
  return api.post(`/admin/ingestions/detect-backfill`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
};
export const getSignedDocUrl = (key, ttl = 300) => api.get(`/admin/counselling/signed-url`, { params: { key, ttl } });
export const checkSmFormat = (url) => api.post(`/admin/counselling-docs/sm-format-check`, { url }, { timeout: 120000 });
export const ingestFeesDoc = (url) => api.post(`/admin/counselling-docs/ingest-fees`, { url }, { timeout: 120000 });
export const diaryIngestFees = (ingestId) => api.post(`/admin/diary/ingest-fees`, { ingest_id: ingestId }, { timeout: 120000 });

// Phase 3d — counselling document lifecycle
export const getCounsellingDocs = (params = {}) => api.get(`/admin/counselling-docs`, { params });
export const approveCounsellingDocs = (urls, ingest = true) => api.post(`/admin/counselling-docs/approve`, { urls, ingest }, { timeout: 180000 });
export const smIngestPlan = (url) => api.post(`/admin/counselling-docs/sm-ingest-plan`, { url });
export const ingestSmWithRound = (url, matrix_type) => api.post(`/admin/counselling-docs/ingest-sm`, { url, matrix_type }, { timeout: 180000 });
export const rejectCounsellingDocs = (urls) => api.post(`/admin/counselling-docs/reject`, { urls });
export const setCounsellingDocHint = (url, hint) => api.post(`/admin/counselling-docs/hint`, { url, hint });
export const setCounsellingDocType = (url, doc_type) => api.post(`/admin/counselling-docs/set-type`, { url, doc_type });
export const ingestCounsellingYear = (year, body) => api.post(`/admin/counselling-docs/ingest-year`, { year, body });
export const manualDiscoverCounselling = (text, bypass_domain = false) => api.post(`/admin/counselling-docs/manual-discover`, { text, bypass_domain }, { timeout: 120000 });
export const setCounsellingAutoApprove = (enabled) => api.post(`/admin/counselling-docs/auto-approve`, { enabled });
export const runSeasonPrimer = (year) => api.post(`/admin/counselling/season-primer`, { year }, { timeout: 60000 });
export const runEodSweep = () => api.post(`/admin/counselling/run-eod`, {});

// Phase C — counselling schedules
export const parseSchedule = (url) => api.post(`/admin/counselling/parse-schedule`, { url }, { timeout: 120000 });
export const setScheduleVisibility = (url, hidden) => api.post(`/admin/counselling/schedule-visibility`, { url, hidden });
export const classifyCounselling = () => api.post(`/admin/counselling/classify`, {});
export const relinkCounselling = () => api.post(`/admin/counselling/relink`, {}, { timeout: 60000 });
export const getSchedulesList = (body) => api.get(`/counselling/schedules`, { params: body ? { body } : {} });
export const getSchedule = (bodySlug, slug) => api.get(`/counselling/schedule/${bodySlug}/${slug}`);
export const setScheduleTitle = (body_slug, slug, title) => api.post(`/admin/schedule/set-title`, { body_slug, slug, title });
export const setScheduleRoundLabel = (body_slug, slug, round_key, label) => api.post(`/admin/schedule/set-round-label`, { body_slug, slug, round_key, label });
export const setScheduleEventLinks = (body_slug, slug, event_key, links) => api.post(`/admin/schedule/event-links`, { body_slug, slug, event_key, links });
export const seedDemoSnapshot = () => api.post(`/admin/counselling/seed-demo`, {});
export const clearDemoSnapshot = () => api.post(`/admin/counselling/clear-demo`, {});

// College News & Events (TV channel board)
export const getNewsStates = () => api.get(`/news/states`);
export const getNewsColleges = (state) => api.get(`/news/colleges`, { params: { state } });
export const getNewsFeed = ({ state, college_code, window, limit }) => api.get(`/news/feed`, { params: { state, college_code, window, limit } });
export const seedDemoNews = () => api.post(`/admin/news/seed-demo`, {});
export const clearDemoNews = () => api.post(`/admin/news/clear-demo`, {});
export const subscribeNews = (email, scope, news_state) =>
  api.post(`/news/subscribe`, { email, scope, news_state, base_url: typeof window !== "undefined" ? window.location.origin : undefined });
export const unsubscribeNews = (email, scope, news_state) => api.post(`/news/unsubscribe`, { email, scope, news_state });
export const getSubscription = (email, scope, news_state) => api.get(`/news/subscription`, { params: { email, scope, news_state } });
export const getCrawlerSchedules = () => api.get(`/admin/crawler-schedules`);
export const setCrawlerSchedule = (payload) => api.put(`/admin/crawler-schedules`, payload);
export const NEWS_STREAM_URL = `${API}/news/stream`;


// Phase C — College Yearly Stats (precomputed)
export const getStatsSummary = (year) => api.get(`/college-yearly-stats/summary`, { params: year ? { year } : {} });
export const getNewColleges = (year, strict = true, limit = 200) => api.get(`/college-yearly-stats/new-colleges`, { params: { year, strict, limit } });
export const getSeatGainers = (year, limit = 200) => api.get(`/college-yearly-stats/seat-gainers`, { params: { year, limit } });
export const getSeatDroppers = (year, limit = 200) => api.get(`/college-yearly-stats/seat-droppers`, { params: { year, limit } });
export const getCollegeHistory = (code) => api.get(`/college-yearly-stats/history/${encodeURIComponent(code)}`);

// Homepage Fresh Colleges rail — lead capture (fee enquiry)
export const submitLead = (payload) => api.post(`/leads`, payload);

// ─── Admin: NMC raw seat-matrix (Step 1 of validation-gated re-ingest) ───
export const getNmcRawIngests = (year, limit = 50) =>
  api.get(`/admin/nmc/raw-ingests`, { params: { ...(year ? { year } : {}), limit } });
export const getNmcRawSummary = (ingest_id) =>
  api.get(`/admin/nmc/raw-summary/${encodeURIComponent(ingest_id)}`);
export const getNmcRawRows = (ingest_id, params = {}) =>
  api.get(`/admin/nmc/raw-rows`, { params: { ingest_id, ...params } });
export const nmcRawRowsCsvUrl = (ingest_id) =>
  `${API}/admin/nmc/raw-rows/export.csv?ingest_id=${encodeURIComponent(ingest_id)}`;
export const nmcResetYear = (year, dry_run = true) =>
  api.post(`/admin/nmc/reset-year`, { year, dry_run });
// Step 2 HITL — table-parsing hint + validation-gated re-ingest
export const setNmcTableHint = (url, hint) =>
  api.post(`/admin/counselling-docs/table-hint`, { url, hint });
export const reingestNmcDoc = (url, use_hint = true) =>
  api.post(`/admin/nmc/reingest`, { url, use_hint });
export const getNmcValidation = (ingest_id) =>
  api.get(`/admin/nmc/validation/${encodeURIComponent(ingest_id)}`);
export const nmcRematchStart = (ingest_id, dry_run = true, sr_nos = null) =>
  api.post(`/admin/nmc/rematch-and-apply`, { ingest_id, dry_run, ...(sr_nos ? { sr_nos } : {}) });
export const nmcRematchStatus = (job_id) =>
  api.get(`/admin/nmc/rematch-status/${encodeURIComponent(job_id)}`);
export const nmcHeuristicPreview = (ingest_id, min_score = 95, version = "v1") =>
  api.post(`/admin/nmc/heuristic-preview`, { ingest_id, min_score, version }, { timeout: 60000 });
export const nmcLlmMatchPreview = (ingest_id, version = "v1") =>
  api.post(`/admin/nmc/llm-match-preview`, { ingest_id, version }, { timeout: 300000 });
export const nmcLlmMatchConfirm = (ingest_id, sr_no, college_code) =>
  api.post(`/admin/nmc/llm-match-confirm`, { ingest_id, sr_no, college_code });
export const adminCollegesLiteSearch = (q, state, exclude_ingest_id) =>
  api.get(`/admin/colleges/lite-search`, { params: { q, state, ...(exclude_ingest_id ? { exclude_ingest_id } : {}) } });
export const assignDiaryFee = (ingest_id, college_code, row, columns) =>
  api.post(`/admin/diary/fees/assign`, { ingest_id, college_code, row, ...(columns ? { columns } : {}) });
export const setAgentFeesColumns = (session_id, columns) =>
  api.post(`/admin/agent/fees-columns`, { session_id, columns });
export const setAgentFeesApplyOpts = (session_id, opts) =>
  api.post(`/admin/agent/fees-columns`, { session_id, ...opts });
export const setCollegeFee = (payload) => api.post(`/admin/college-fee/set`, payload);
export const moveCollegeFee = (payload) => api.post(`/admin/college-fee/move`, payload);
export const removeCollegeFeeYear = (payload) => api.post(`/admin/college-fee/remove-year`, payload);
export const nmcCreateNewColleges = (ingest_id, sr_nos, dry_run = true) =>
  api.post(`/admin/nmc/create-new-colleges`, { ingest_id, sr_nos, dry_run });

// ─── Generalized "update key" cascade + independent Apply workflow ───────
export const nmcComputeUpdateKeysStart = (ingest_id, heuristic_min_score = 95) =>
  api.post(`/admin/nmc/compute-update-keys`, { ingest_id, heuristic_min_score });
export const nmcComputeUpdateKeysStatus = (job_id) =>
  api.get(`/admin/nmc/update-keys-status/${encodeURIComponent(job_id)}`);
export const nmcUpdateKeySummary = (ingest_id) =>
  api.get(`/admin/nmc/update-key-summary`, { params: { ingest_id } });
export const nmcUpdateKeySrNos = (ingest_id, stage) =>
  api.get(`/admin/nmc/update-key-sr-nos`, { params: { ingest_id, stage } });
export const nmcApplyUpdateKeys = (ingest_id, sr_nos, columns, learn_alias = true) =>
  api.post(`/admin/nmc/apply-update-keys`, { ingest_id, sr_nos, columns, learn_alias });
export const nmcManualUpdateKey = (ingest_id, sr_no, college_code) =>
  api.post(`/admin/nmc/manual-update-key`, { ingest_id, sr_no, college_code });

// ─── Admin: Data Health — shared/generic alias purge ───
export const purgeSharedAlias = (alias_norm, codes = null) =>
  api.post(`/admin/data-health/purge-alias`, { alias_norm, ...(codes ? { codes } : {}) }, { timeout: 30000 });
export const resolveAiimsShortforms = () =>
  api.post(`/admin/data-health/resolve-aiims-shortforms`, {}, { timeout: 30000 });
export const resolveGmcShortforms = () =>
  api.post(`/admin/data-health/resolve-gmc-shortforms`, {}, { timeout: 30000 });
export const resolveMgmMaharashtra = () =>
  api.post(`/admin/data-health/resolve-mgm-maharashtra`, {}, { timeout: 60000 });
export const resolveScbCuttack = () =>
  api.post(`/admin/data-health/resolve-scb-cuttack`, {}, { timeout: 30000 });
export const resolveSonbhadraAsmc = () =>
  api.post(`/admin/data-health/resolve-sonbhadra-asmc`, {}, { timeout: 60000 });
export const resolveAcsrNellore = () =>
  api.post(`/admin/data-health/resolve-acsr-nellore`, {}, { timeout: 60000 });
export const resolveGenericTemplateAliases = () =>
  api.post(`/admin/data-health/resolve-generic-template-aliases`, {}, { timeout: 60000 });


